import {logUtishaDebug, logUtishaTrace} from 'src/app/util/log';
import {CanvasRenderer} from '../CanvasRenderer';
import {Timer, TimerOptions} from '../../../views/timer';
import {Square} from '../../../views/model/Square';
import {Subject} from 'rxjs';

/**
 * Renderer for drawing a Timer on a canvas
 */
export class TimerCanvasRenderer extends CanvasRenderer {
  private timer: Timer | null = null;
  private timerStopped$ = new Subject<Timer>();

  /**
   * Creates a new TimerCanvasRenderer
   * @param options Timer options or duration in milliseconds
   * @param _priority Drawing priority (higher means drawn later/on top)
   */
  constructor(
    private options: TimerOptions | number,
    _priority = 50 // Higher priority to render on top
  ) {
    super(_priority);
  }

  /**
   * Initializes the renderer with a canvas
   */
  initialize(canvas: HTMLCanvasElement): void {
    super.initialize(canvas);
    logUtishaDebug(`TimerCanvasRenderer[${this.id}]: initialized with canvas ${canvas.width}x${canvas.height}`);

    // Create timer options
    let timerOptions: TimerOptions;

    if (typeof this.options === 'number') {
      // Convert number to TimerOptions
      const timerSize = 150;
      const timerPosition = new Square({
        x: (canvas.width - timerSize) / 2, // Center horizontally
        y: (canvas.height - timerSize) / 2, // Center vertically
      }, timerSize);

      timerOptions = {
        timeMs: this.options,
        dimensions: timerPosition,
        beepOn: [1, 2, 3],
        onStopped: (timer) => this.timerComplete(timer)
      };
    } else {
      // Use provided options
      timerOptions = {
        ...this.options,
        onStopped: (timer) => this.timerComplete(timer)
      };

      // Default position in center of canvas if not specified
      if (!timerOptions.dimensions) {
        const timerSize = 150;
        timerOptions.dimensions = new Square({
          x: (canvas.width - timerSize) / 2,
          y: (canvas.height - timerSize) / 2,
        }, timerSize);
      }
    }

    // Create and configure timer
    this.timer = new Timer(canvas, timerOptions);
    if (typeof this.timer.setExternallyRendered === 'function') {
      this.timer.setExternallyRendered(true);
    }
  }

  /**
   * Called when the timer completes
   * @param timer The timer that completed
   */
  private timerComplete(timer: Timer): void {
    logUtishaDebug(`TimerCanvasRenderer[${this.id}]: timer complete`);
    if (typeof this.options === 'object' && typeof this.options.onStopped === 'function') {
      this.options.onStopped(timer);
    }
    // Emit event before destroying
    this.destroy();
  }

  /**
   * Renders the timer to the canvas
   */
  render(): void {
    logUtishaTrace(`TimerCanvasRenderer[${this.id}]: rendering`);
    if (!this.timer) return;

    try {
      this.timer.render();
    } catch (error) {
      logUtishaDebug(`TimerCanvasRenderer[${this.id}]: error rendering - ${error}`);
    }
  }

  /**
   * Gets the name of this renderer
   */
  get name(): string {
    return TimerCanvasRenderer.NAME;
  }

  static get NAME() {
    return 'TimerRenderer';
  }
}
